import React, { useEffect, useMemo, useState } from 'react';
import AccountLayout from '../components/account/AccountLayout';
import { useAccountCounts } from '../components/account/useAccountCounts';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { apiService } from '../services/api';
import { errorMessage } from '../services/manageApi';
import { NotificationItem } from '../types/interfaces';
import { emitNotificationsUpdated } from '../utils/notificationEvents';
import RelativeTime from '../components/common/RelativeTime';
import { formatDateTime } from '../utils/dates';
import './ProfileNotifications.css';

type NotificationFilter = 'all' | 'unread' | 'completed';

// Same rule as the server: completed notifications, and read ones that are not
// tasks, can be deleted. Unread items and open tasks are kept.
const isClearable = (item: NotificationItem): boolean =>
  item.status === 'completed' || (item.status === 'read' && !item.actionable);

const CLEAR_ALL_ID = -2;

const ProfileNotifications: React.FC = () => {
  const { data } = useAccountCounts();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const [loading, setLoading] = useState(true);
  const [workingIds, setWorkingIds] = useState<number[]>([]);
  const [error, setError] = useState('');

  const loadNotifications = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.get('/notifications/mine');
      const payload = Array.isArray(response.data) ? response.data : [];
      setNotifications(payload);
      emitNotificationsUpdated();
    } catch (err) {
      setError(errorMessage(err, 'Failed to load notifications'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((item) => item.status === 'unread').length,
    [notifications]
  );

  const clearableCount = useMemo(() => notifications.filter(isClearable).length, [notifications]);

  const filteredNotifications = useMemo(() => {
    if (filter === 'all') return notifications;
    if (filter === 'unread') return notifications.filter((item) => item.status === 'unread');
    return notifications.filter((item) => item.status === 'completed');
  }, [filter, notifications]);

  const markRead = async (notificationId: number) => {
    setWorkingIds((prev) => [...prev, notificationId]);
    setError('');
    try {
      await apiService.post(`/notifications/${notificationId}/mark_read`, {});
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId && item.status === 'unread'
            ? { ...item, status: 'read', read_at: new Date().toISOString() }
            : item
        )
      );
      emitNotificationsUpdated();
    } catch (err) {
      setError(errorMessage(err, 'Failed to mark notification as read'));
    } finally {
      setWorkingIds((prev) => prev.filter((id) => id !== notificationId));
    }
  };

  const markCompleted = async (notificationId: number) => {
    setWorkingIds((prev) => [...prev, notificationId]);
    setError('');
    try {
      await apiService.post(`/notifications/${notificationId}/mark_completed`, {});
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId
            ? {
                ...item,
                status: 'completed',
                read_at: item.read_at || new Date().toISOString(),
                completed_at: item.completed_at || new Date().toISOString(),
              }
            : item
        )
      );
      emitNotificationsUpdated();
    } catch (err) {
      setError(errorMessage(err, 'Failed to mark notification as completed'));
    } finally {
      setWorkingIds((prev) => prev.filter((id) => id !== notificationId));
    }
  };

  const markAllRead = async () => {
    setWorkingIds((prev) => [...prev, -1]);
    setError('');
    try {
      await apiService.post('/notifications/mark_all_read', {});
      setNotifications((prev) =>
        prev.map((item) =>
          item.status === 'unread' ? { ...item, status: 'read', read_at: new Date().toISOString() } : item
        )
      );
      emitNotificationsUpdated();
    } catch (err) {
      setError(errorMessage(err, 'Failed to mark all notifications as read'));
    } finally {
      setWorkingIds((prev) => prev.filter((id) => id !== -1));
    }
  };

  const clearReadAndCompleted = async () => {
    const confirmed = window.confirm(
      `Delete ${clearableCount} read or completed notification(s)? Unread notifications and open tasks are kept.`
    );
    if (!confirmed) return;
    setWorkingIds((prev) => [...prev, CLEAR_ALL_ID]);
    setError('');
    try {
      await apiService.post('/notifications/clear', {});
      setNotifications((prev) => prev.filter((item) => !isClearable(item)));
    } catch (err) {
      setError(errorMessage(err, 'Failed to clear notifications'));
    } finally {
      setWorkingIds((prev) => prev.filter((id) => id !== CLEAR_ALL_ID));
    }
  };

  const deleteNotification = async (notificationId: number) => {
    setWorkingIds((prev) => [...prev, notificationId]);
    setError('');
    try {
      await apiService.delete(`/notifications/${notificationId}`);
      setNotifications((prev) => prev.filter((item) => item.id !== notificationId));
    } catch (err) {
      setError(errorMessage(err, 'Failed to delete notification'));
    } finally {
      setWorkingIds((prev) => prev.filter((id) => id !== notificationId));
    }
  };

  if (loading) {
    return (
      <AccountLayout title="Notifications" data={data}>
        <div className="profile-notifications__loader">
          <LoadingSpinner size="md" text="Loading notifications..." />
        </div>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout title="Notifications" data={data}>
      <section className="profile-notifications__filters" data-tour="notif-filters">
        <button
          className={`profile-notifications__filter ${filter === 'all' ? 'profile-notifications__filter--active' : ''}`}
          onClick={() => setFilter('all')}
          type="button"
        >
          All ({notifications.length})
        </button>
        <button
          className={`profile-notifications__filter ${filter === 'unread' ? 'profile-notifications__filter--active' : ''}`}
          onClick={() => setFilter('unread')}
          type="button"
        >
          Unread ({unreadCount})
        </button>
        <button
          className={`profile-notifications__filter ${filter === 'completed' ? 'profile-notifications__filter--active' : ''}`}
          onClick={() => setFilter('completed')}
          type="button"
        >
          Completed ({notifications.filter((item) => item.status === 'completed').length})
        </button>
        <Button variant="ghost" size="sm" onClick={loadNotifications}>
          Refresh
        </Button>
        <div className="profile-notifications__toolbar" data-tour="notif-bulk">
          <Button
            variant="secondary"
            size="sm"
            onClick={markAllRead}
            disabled={workingIds.includes(-1) || unreadCount === 0}
          >
            Mark All Read
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={clearReadAndCompleted}
            disabled={workingIds.includes(CLEAR_ALL_ID) || clearableCount === 0}
          >
            Clear read &amp; completed ({clearableCount})
          </Button>
        </div>
      </section>

      {error && <div className="profile-notifications__error">{error}</div>}

      {filteredNotifications.length === 0 ? (
        <div className="profile-notifications__empty" data-tour="notif-empty">
          <p>No notifications.</p>
          {filter !== 'all' && (
            <Button variant="ghost" size="sm" onClick={() => setFilter('all')}>
              Show all
            </Button>
          )}
        </div>
      ) : (
        <section className="profile-notifications__list" data-tour="notif-list">
          {filteredNotifications.map((item) => {
            const isWorking = workingIds.includes(item.id);
            const canComplete = item.actionable && item.status !== 'completed';
            return (
              <article key={item.id} className={`profile-notifications__card profile-notifications__card--${item.status}`}>
                <header className="profile-notifications__card-header">
                  <div>
                    <h3>{item.title}</h3>
                    <p className="profile-notifications__meta-line">
                      <span className="profile-notifications__source">{item.source_label}</span>
                      <span>Sent by {item.sender_nickname}</span>
                      <RelativeTime value={item.created_at} />
                    </p>
                  </div>
                  <span className={`profile-notifications__status profile-notifications__status--${item.status}`}>
                    {item.status}
                  </span>
                </header>

                <p className="profile-notifications__message">{item.message}</p>

                <div className="profile-notifications__detail-row">
                  {item.scope === 'competition' ? (
                    <span>
                      <strong>Competition:</strong> {item.competition_title || `#${item.competition_id}`}
                    </span>
                  ) : (
                    <span>
                      <strong>Scope:</strong> Global
                    </span>
                  )}
                  {item.due_at && (
                    <span>
                      <strong>Due:</strong> {formatDateTime(item.due_at)}
                    </span>
                  )}
                </div>

                <div className="profile-notifications__actions">
                  {item.status === 'unread' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => markRead(item.id)}
                      disabled={isWorking}
                    >
                      Mark Read
                    </Button>
                  )}

                  {canComplete && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => markCompleted(item.id)}
                      disabled={isWorking}
                    >
                      Mark Completed
                    </Button>
                  )}

                  {item.action_url && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => window.open(item.action_url || '', '_blank', 'noopener,noreferrer')}
                    >
                      {item.action_label || 'Open link'}
                    </Button>
                  )}

                  {isClearable(item) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="profile-notifications__delete"
                      onClick={() => deleteNotification(item.id)}
                      disabled={isWorking}
                      aria-label={`Delete notification: ${item.title}`}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      )}
    </AccountLayout>
  );
};

export default ProfileNotifications;
